// Slot defaults canonically live at rest as element expressions in a component's
// `defaultData`, and render nested inside the owning instance with the
// definition's authored ids. Unmigrated definitions can still store them on the
// SlotContent field's `system.defaultValue`. This file is the one place that
// reads, finds, and operates on them — consumers should not walk either shape by
// hand.
// Exception: publish walks the serialized JSON before components are reified,
// and importing this file from `@packages/systems/core` would be a cycle, so
// `getSlotDefaultSymbolIds` in JsonDomUtils reads the at-rest shapes there. It
// parses `defaultData` and reuses the shared element traversal, so only its walk
// of the legacy `defaultValue` POJOs has to stay in sync with this file.
import {
  Expressions,
  Plugins,
  Component,
  EvalEnv,
  Types,
  getSystem,
  type ExpressionPath,
  type Type,
} from '@packages/systems/wfdl';
import {
  EElement,
  EList,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {isNodePojo, type NodePojo} from '@packages/systems/core/nodepojo';
import {nodePojoToElementWithComponents} from '@packages/systems/core/component-conversions';
import {
  copyInstanceSuffix,
  getGuidNullable,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  getName,
  getPluginName,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import {emptyArray} from '@packages/utilities/fp/utils';
import {isSlot} from '@packages/utilities/elementTypeUtils';
import {cacheMemo, memoize, weakMemo} from '@packages/utilities/memo';
import {
  getComponentInstRootElement,
  getPageComponentRootElement,
} from '@packages/utilities/core-elements';
import {
  emptySlotDefaultList,
  getSlotDefaultElementsByFieldId,
} from '@packages/utilities/platform-slots';

export {
  getSlotDefaultExpression,
  setSlotDefaultExpression,
  hasSlotDefaultContent,
} from '@packages/utilities/platform-slots';

// An unmigrated definition still stores its default as POJOs on the field.
const getSlotContentDefaultPojos = (fieldType: Type): NodePojo[] | null => {
  if (!Types.isSlotContentType(fieldType)) return null;
  const defaultValue = getSystem(fieldType)?.defaultValue;
  if (!Array.isArray(defaultValue) || defaultValue.length === 0) return null;
  return defaultValue;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

// Reads the at-rest POJO markup, so it needs no plugins or reification.
const forEachLegacySlotDefaultComponentNode = (
  value: unknown,
  visit: (id: string, typeName: string) => void
) => {
  if (Array.isArray(value)) {
    for (const item of value)
      forEachLegacySlotDefaultComponentNode(item, visit);
    return;
  }
  if (!isRecord(value)) return;
  if (
    isNodePojo(value) &&
    value.pluginType === '__SitePlugin' &&
    typeof value.type === 'string'
  ) {
    visit(value.id, value.type);
  }
  for (const nestedValue of Object.values(value)) {
    forEachLegacySlotDefaultComponentNode(nestedValue, visit);
  }
};

// Visits every __SitePlugin component instance authored across a component's
// SlotContent field defaults, at any depth, in document order. Covers both
// at-rest shapes: whatever the render can materialize, dependency walks have to
// see, or the definition gets pruned out from under its own default.
const forEachSlotDefaultComponentNode = (
  component: Component.ComponentType,
  visit: (id: string, typeName: string) => void
) => {
  const fields = Types.getRecordFields(Component.getDataType(component));
  if (!fields) return;
  const defaults = getSlotDefaultElementsByFieldId(component);

  for (const [fieldId, fieldType] of Object.entries(fields)) {
    if (!Types.isSlotContentType(fieldType)) continue;

    const defaultElements = defaults.get(fieldId);
    if (!defaultElements) {
      forEachLegacySlotDefaultComponentNode(
        getSystem(fieldType)?.defaultValue,
        visit
      );
      continue;
    }

    for (const root of defaultElements) {
      for (const element of Object.values(
        Expressions.allElements(EElement(root))
      )) {
        if (getPluginName(element.type) !== '__SitePlugin') continue;
        visit(element.id, getName(element.type));
      }
    }
  }
};

/**
 * Component types referenced by a component's SlotContent field defaults.
 * Defaults are stored on the definition's defaultData rather than its render, so
 * render walks must include these edges explicitly.
 */
export const getSlotDefaultComponentTypes = weakMemo(
  (component: Component.ComponentType) => {
    const seen = new Set<string>();
    const types: Array<ExpressionElement['type']> = [];
    forEachSlotDefaultComponentNode(component, (_id, typeName) => {
      if (!seen.has(typeName)) {
        seen.add(typeName);
        types.push(
          qualify(Plugins.name('__SitePlugin'), Component.name(typeName))
        );
      }
    });
    return types;
  }
);

/**
 * Component instances authored across a component's SlotContent field defaults,
 * as [authored id, component type] pairs. Every authored placement is included,
 * so a component authored twice in a default appears twice. Backs both the
 * Components-panel count and the Show Instances list for slot defaults.
 */
export const getSlotDefaultComponentInstances = weakMemo(
  (
    component: Component.ComponentType
  ): Array<[string, ExpressionElement['type']]> => {
    const instances: Array<[string, ExpressionElement['type']]> = [];
    forEachSlotDefaultComponentNode(component, (id, typeName) => {
      instances.push([
        id,
        qualify(Plugins.name('__SitePlugin'), Component.name(typeName)),
      ]);
    });
    return instances;
  }
);

export const addsSlotDefaultComponentReference = (
  before: Component.ComponentType,
  after: Component.ComponentType
): boolean => {
  const previousReferences = new Set(
    getSlotDefaultComponentTypes(before).map(getName)
  );
  return getSlotDefaultComponentTypes(after).some(
    (componentType) => !previousReferences.has(getName(componentType))
  );
};

// Callers reading only `defaultData` can pass null. This object gives those
// calls a separate weakMemo cache from calls that use plugins.
//
// Plugins are still needed to convert legacy `system.defaultValue` defaults
// used by AI site generation. We can remove them once defaultData is fully released.
const NO_PLUGINS_KEY: {readonly __noPlugins: true} = {__noPlugins: true};
type PluginsMemoKey = Plugins.PluginsType | typeof NO_PLUGINS_KEY;
const pluginsMemoKey = (plugins: Plugins.PluginsType | null): PluginsMemoKey =>
  plugins ?? NO_PLUGINS_KEY;
const isNoPlugins = (key: PluginsMemoKey): key is typeof NO_PLUGINS_KEY =>
  key === NO_PLUGINS_KEY;

// Memo keys on the field Type object — edits to the default replace the type.
const reifyLegacySlotDefaultElements = weakMemo((pluginsKey: PluginsMemoKey) =>
  weakMemo((components: Component.ComponentMap) =>
    weakMemo((fieldType: Type): ExpressionElement[] => {
      if (isNoPlugins(pluginsKey)) return emptyArray;
      const defaultPojos = getSlotContentDefaultPojos(fieldType);
      if (!defaultPojos) return emptyArray;
      return defaultPojos.map((pojo) =>
        nodePojoToElementWithComponents(pluginsKey)(components)(pojo)
      );
    })
  )
);

// A slot field's default content as expression elements. Ids are the definition's
// authored ids — the render never mints per-instance copies, so these match what's
// on canvas.
export const getSlotDefaultElements = (
  plugins: Plugins.PluginsType | null,
  components: Component.ComponentMap,
  ownerComponentType: ExpressionElement['type'],
  slotFieldId: string
): ExpressionElement[] => {
  const ownerComponent = components.get(ownerComponentType);
  if (!ownerComponent) return emptyArray;
  const fieldType = Types.getRecordFields(
    Component.getDataType(ownerComponent)
  )?.[slotFieldId];
  if (!fieldType) return emptyArray;
  const defaultElements =
    getSlotDefaultElementsByFieldId(ownerComponent).get(slotFieldId);
  if (defaultElements) return defaultElements;
  return reifyLegacySlotDefaultElements(pluginsMemoKey(plugins))(components)(
    fieldType
  );
};

export type SlotDefaultHit = {
  element: ExpressionElement;
  slotFieldId: string;
  defaultElements: ExpressionElement[];
};

// Reading through to the definition costs a reify plus a fresh EList, and a fresh
// expression means a cold `allElements` cache for every reader downstream. Cache
// per component field so the identity — and those caches — stay warm.
const memoizedDefinitionSlotDefaultExpression = weakMemo(
  (pluginsKey: PluginsMemoKey) =>
    weakMemo((components: Component.ComponentMap) =>
      weakMemo((componentType: ExpressionElement['type']) =>
        cacheMemo(32)((fieldId: string): Expression | null => {
          const component = components.get(componentType);
          const fieldType = component
            ? Types.getRecordFields(Component.getDataType(component))?.[fieldId]
            : null;
          if (!component || !fieldType || !Types.isSlotContentType(fieldType)) {
            return null;
          }

          const defaultElements = getSlotDefaultElements(
            isNoPlugins(pluginsKey) ? null : pluginsKey,
            components,
            componentType,
            fieldId
          );
          if (defaultElements.length === 0) return emptySlotDefaultList;
          return EList(defaultElements.map((element) => EElement(element)));
        })
      )
    )
);

/**
 * A slot's effective content, and where it came from.
 *
 * `override` is authored on the instance itself. `default` is the component's
 * shared value, which every instance projects, so writing it anywhere other than
 * that component's own definition produces a copy.
 */
export type EffectiveSlotDefault =
  | {origin: 'override'; expression: Expression}
  | {origin: 'default'; expression: Expression};

export const resolveEffectiveSlotDefault = (
  plugins: Plugins.PluginsType | null,
  components: Component.ComponentMap,
  componentType: ExpressionElement['type'],
  fieldId: string,
  instance?: ExpressionElement | null
): EffectiveSlotDefault | null => {
  const override = instance
    ? Expressions.getIn(Expressions.getElementData(instance), [
        {in: 'Record', at: fieldId},
      ])
    : null;
  if (override) return {origin: 'override', expression: override};

  const expression = memoizedDefinitionSlotDefaultExpression(
    pluginsMemoKey(plugins)
  )(components)(componentType)(fieldId);
  if (!expression) return null;
  return {origin: 'default', expression};
};

export const getEffectiveSlotDefaultExpression = (
  plugins: Plugins.PluginsType | null,
  components: Component.ComponentMap,
  componentType: ExpressionElement['type'],
  fieldId: string,
  instance?: ExpressionElement | null
): Expression | null =>
  resolveEffectiveSlotDefault(
    plugins,
    components,
    componentType,
    fieldId,
    instance
  )?.expression ?? null;

// The hit's element comes from the effective expression, so its sibling list has to
// come from there too, or an override hit gets paired with definition defaults.
const slotDefaultElementsFromExpression = (
  expression: Expression
): ExpressionElement[] => {
  const items = Expressions.getList(expression);
  if (!items) return emptyArray;
  const elements: ExpressionElement[] = [];
  for (const item of items) {
    const element = Expressions.getElement(item);
    if (element) elements.push(element);
  }
  return elements;
};

const findSlotDefaultElementInFields = (
  plugins: Plugins.PluginsType | null,
  components: Component.ComponentMap,
  componentType: ExpressionElement['type'],
  elementId: string,
  instance?: ExpressionElement | null
): SlotDefaultHit | null => {
  const component = components.get(componentType);
  if (!component) return null;
  const fields = Types.getRecordFields(Component.getDataType(component));
  if (!fields) return null;
  for (const [slotFieldId, fieldType] of Object.entries(fields)) {
    if (!Types.isSlotContentType(fieldType)) continue;
    const expression = getEffectiveSlotDefaultExpression(
      plugins,
      components,
      componentType,
      slotFieldId,
      instance
    );
    if (!expression) continue;
    const element = Expressions.getElementById(expression, elementId);
    if (element) {
      return {
        element,
        slotFieldId,
        defaultElements: slotDefaultElementsFromExpression(expression),
      };
    }
  }
  return null;
};

// Every field scan reifies and walks; path classification and the post-move
// search both ask the same component the same question repeatedly.
const memoizedFindDefinitionSlotDefaultElement = weakMemo(
  (pluginsKey: PluginsMemoKey) =>
    weakMemo((components: Component.ComponentMap) =>
      weakMemo((componentType: ExpressionElement['type']) =>
        cacheMemo(64)((elementId: string) =>
          findSlotDefaultElementInFields(
            isNoPlugins(pluginsKey) ? null : pluginsKey,
            components,
            componentType,
            elementId
          )
        )
      )
    )
);

// Scope is one component's SlotContent fields — the path's owning-instance segment tells
// us which component, so no global scan.
export const findSlotDefaultElement = (
  plugins: Plugins.PluginsType | null,
  components: Component.ComponentMap,
  componentType: ExpressionElement['type'],
  elementId: string,
  instance?: ExpressionElement | null
): SlotDefaultHit | null => {
  if (!instance) {
    return memoizedFindDefinitionSlotDefaultElement(pluginsMemoKey(plugins))(
      components
    )(componentType)(elementId);
  }

  return findSlotDefaultElementInFields(
    plugins,
    components,
    componentType,
    elementId,
    instance
  );
};

export type SlotDefaultExpressionTreeHit = SlotDefaultHit & {
  ignoredInstanceOverride: boolean;
};

export const findSlotDefaultElementAcrossTrees = (
  plugins: Plugins.PluginsType | null,
  components: Component.ComponentMap,
  componentType: ExpressionElement['type'],
  elementId: string,
  instance?: ExpressionElement | null
): SlotDefaultExpressionTreeHit | null => {
  const effectiveHit = findSlotDefaultElement(
    plugins,
    components,
    componentType,
    elementId,
    instance
  );
  if (effectiveHit) {
    return {...effectiveHit, ignoredInstanceOverride: false};
  }
  if (!instance) return null;

  const definitionHit = findSlotDefaultElement(
    plugins,
    components,
    componentType,
    elementId
  );
  if (!definitionHit) return null;

  return {...definitionHit, ignoredInstanceOverride: true};
};

export type SlotDefaultPathContext = {
  ownerInstancePath: NativeId[];
  ownerComponentType: ExpressionElement['type'];
  slotFieldId: string;
  ignoreOwnerOverride?: true;
};

// Classifies a native id path as targeting nested slot-default content. Walks every
// segment live-first: resolve off the current or initial root, and cross into an
// instance's root to continue. The first segment that resolves ONLY via the owning
// component's SlotContent defaults marks the path as default content rooted at that
// owner instance. Null when every segment resolves live, or when the path can't be
// classified (no owner known when a segment misses).
const resolveSlotDefaultPathContextUncached = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  targetedSlotFieldId: string | null,
  focusDepth: number
): SlotDefaultPathContext | null => {
  const initialRootElem = getPageComponentRootElement(componentMap);
  if (!initialRootElem) return null;

  const initialRootExpr = EElement(initialRootElem);
  let currentRootExpr: Expression | null = initialRootExpr;
  let currentOwnerType: ExpressionElement['type'] | null = null;
  let currentOwnerElement: ExpressionElement | null = null;

  for (let index = 0; index < nativeIdPath.length; index++) {
    const segmentId = getGuidNullable(nativeIdPath[index]);
    if (!segmentId) return null;

    let fromCurrentRoot: ExpressionElement | null = null;
    if (currentRootExpr) {
      fromCurrentRoot = Expressions.getElementById(currentRootExpr, segmentId);
    }
    let fromDefaults: SlotDefaultExpressionTreeHit | null = null;
    if (!fromCurrentRoot && currentOwnerType) {
      fromDefaults = findSlotDefaultElementAcrossTrees(
        plugins,
        componentMap,
        currentOwnerType,
        segmentId,
        currentOwnerElement
      );
    }
    if (fromDefaults && currentOwnerType && index >= focusDepth) {
      const context: SlotDefaultPathContext = {
        ownerInstancePath: nativeIdPath.slice(0, index),
        ownerComponentType: currentOwnerType,
        slotFieldId: fromDefaults.slotFieldId,
      };
      if (fromDefaults.ignoredInstanceOverride) {
        context.ignoreOwnerOverride = true;
      }
      return context;
    }
    // Defaults inside the focus prefix only locate the definition being edited.
    const liveElement =
      fromCurrentRoot ||
      Expressions.getElementById(initialRootExpr, segmentId) ||
      (fromDefaults?.element ?? null);

    if (liveElement) {
      let slotFieldId: string | null = null;
      if (
        index === nativeIdPath.length - 1 &&
        currentOwnerType &&
        currentOwnerElement &&
        isSlot(liveElement)
      ) {
        slotFieldId = targetedSlotFieldId;
      }
      const explicitValue =
        slotFieldId && currentOwnerElement
          ? Expressions.getIn(Expressions.getElementData(currentOwnerElement), [
              {in: 'Record', at: slotFieldId},
            ])
          : null;
      const defaultValue =
        slotFieldId && currentOwnerType && currentOwnerElement
          ? getEffectiveSlotDefaultExpression(
              plugins,
              componentMap,
              currentOwnerType,
              slotFieldId,
              currentOwnerElement
            )
          : null;
      if (
        slotFieldId &&
        currentOwnerType &&
        !explicitValue &&
        Expressions.getList(defaultValue ?? emptySlotDefaultList)?.length
      ) {
        return {
          ownerInstancePath: nativeIdPath.slice(0, index),
          ownerComponentType: currentOwnerType,
          slotFieldId,
        };
      }

      const maybeRootElement =
        getComponentInstRootElement(componentMap)(liveElement);
      currentRootExpr = maybeRootElement ? EElement(maybeRootElement) : null;
      currentOwnerType = maybeRootElement ? liveElement.type : null;
      currentOwnerElement = maybeRootElement ? liveElement : null;
      continue;
    }

    return null;
  }

  return null;
};

const memoizedResolveSlotDefaultPathContext = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      // Callers that build the path — Navigator sorting, any sliced sub-path —
      // hand over a new array each call, so keying this level by reference
      // misses for them. '' stands in for "no targeted slot field".
      memoize(
        (
          nativeIdPath: NativeId[],
          targetedSlotFieldId: string,
          focusDepth: number
        ) =>
          resolveSlotDefaultPathContextUncached(
            nativeIdPath,
            componentMap,
            plugins,
            targetedSlotFieldId || null,
            focusDepth
          ),
        8
      )
    )
);

// A Slot anchor only ever arrives as a single Record hop naming its field.
export const getTargetedSlotFieldId = (
  anchorPath?: ExpressionPath | null
): string | null => {
  if (anchorPath?.length !== 1) return null;
  const hop = anchorPath[0];
  if (hop?.in !== 'Record') return null;
  return hop.at;
};

export const resolveSlotDefaultPathContext = (
  nativeIdPath: NativeId[] | undefined,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  anchorPath?: ExpressionPath | null
): SlotDefaultPathContext | null => {
  if (!nativeIdPath?.length) return null;

  return memoizedResolveSlotDefaultPathContext(plugins)(componentMap)(
    nativeIdPath,
    getTargetedSlotFieldId(anchorPath) ?? '',
    0
  );
};

/**
 * `resolveSlotDefaultPathContext` asked from inside the definition being edited.
 *
 * A definition entered through a slot default carries its owners in the focused
 * path, and those owners are how the definition was reached rather than where the
 * path's content is authored. Only defaults below the focused path classify.
 */
export const resolveSlotDefaultPathContextInFocus = (
  nativeIdPath: NativeId[] | undefined,
  focusedSymbolNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  anchorPath?: ExpressionPath | null
): SlotDefaultPathContext | null => {
  if (!nativeIdPath?.length) return null;

  const isInsideFocus = focusedSymbolNativeIdPath.every(
    (focusedNativeId, index) => nativeIdPath[index] === focusedNativeId
  );
  return memoizedResolveSlotDefaultPathContext(plugins)(componentMap)(
    nativeIdPath,
    getTargetedSlotFieldId(anchorPath) ?? '',
    isInsideFocus ? focusedSymbolNativeIdPath.length : 0
  );
};

export type SlotDefaultTargetResolution = {
  element: ExpressionElement;
  componentInstance: ExpressionElement | null;
};

const getSlotDefaultTargetElementOrInstance = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): SlotDefaultTargetResolution | null => {
  const initialRootElement = getPageComponentRootElement(componentMap);
  if (!initialRootElement) return null;

  let currentRoot: ExpressionElement | null = initialRootElement;
  let currentComponentInstance: ExpressionElement | null = null;
  let resolvedElement: ExpressionElement | null = null;
  let resolvedComponentRoot = false;

  for (const nativeId of nativeIdPath) {
    const elementId = getGuidNullable(nativeId);
    if (!elementId) return null;
    resolvedComponentRoot = Boolean(
      currentComponentInstance && currentRoot?.id === elementId
    );

    let element: ExpressionElement | null = currentRoot
      ? Expressions.getElementById(EElement(currentRoot), elementId)
      : null;
    let resolvedFromDefault = false;
    if (!element && currentComponentInstance) {
      element =
        findSlotDefaultElementAcrossTrees(
          plugins,
          componentMap,
          currentComponentInstance.type,
          elementId,
          currentComponentInstance
        )?.element ?? null;
      resolvedFromDefault = Boolean(element);
    }
    if (!element) return null;

    resolvedElement = element;
    const componentRoot = getComponentInstRootElement(componentMap)(element);
    if (componentRoot) {
      currentRoot = componentRoot;
      currentComponentInstance = element;
    } else if (resolvedFromDefault) {
      currentRoot = element;
    }
  }

  if (!resolvedElement) return null;

  if (resolvedComponentRoot && currentComponentInstance) {
    return {
      element: currentComponentInstance,
      componentInstance: currentComponentInstance,
    };
  }

  return {
    element: resolvedElement,
    componentInstance: currentComponentInstance,
  };
};

export type SlotDefaultComponentBoundary = {
  componentRootPath: NativeId[];
  nativeIdPathBoundary: NativeId[];
  // Root path of the outermost instance below the last default hop. Null when
  // there is none or the path ends on a Slot, which keeps its own row.
  defaultInstanceRootPath: NativeId[] | null;
};

export const getLiveElementAtNativeIdPath = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): ExpressionElement | null => {
  const pageRoot = getPageComponentRootElement(componentMap);
  if (!pageRoot) return null;

  let currentRoot = EElement(pageRoot);
  let element: ExpressionElement | null = null;
  for (const nativeId of nativeIdPath) {
    const elementId = getGuidNullable(nativeId);
    if (!elementId) return null;
    element = Expressions.getElementById(currentRoot, elementId);
    if (!element) return null;
    const componentRoot = getComponentInstRootElement(componentMap)(element);
    if (componentRoot) currentRoot = EElement(componentRoot);
  }
  return element;
};

const resolveComponentBoundaryFromSlotDefaultContext = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  context: SlotDefaultPathContext
): SlotDefaultComponentBoundary | null => {
  const ownerComponent = componentMap.get(context.ownerComponentType);
  if (!ownerComponent) return null;

  const ownerRoot = Expressions.getElement(Component.getRender(ownerComponent));
  if (!ownerRoot) return null;

  const ownerInstanceNativeId =
    context.ownerInstancePath[context.ownerInstancePath.length - 1];
  if (!ownerInstanceNativeId) return null;

  let currentRoot: Expression = EElement(ownerRoot);
  let currentOwnerType: ExpressionElement['type'] | null =
    context.ownerComponentType;
  let currentOwnerElement = getLiveElementAtNativeIdPath(
    context.ownerInstancePath,
    componentMap
  );
  if (context.ignoreOwnerOverride) {
    currentOwnerElement = null;
  }
  let componentRootPath = [
    ...context.ownerInstancePath,
    copyInstanceSuffix(ownerInstanceNativeId)(ownerRoot.id),
  ];
  let defaultInstanceRootPath: NativeId[] | null = null;
  let isInsideDefaultContent = false;

  for (
    let pathIndex = context.ownerInstancePath.length;
    pathIndex < nativeIdPath.length;
    pathIndex++
  ) {
    const nativeId = nativeIdPath[pathIndex];
    const elementId = getGuidNullable(nativeId);
    if (!nativeId || !elementId) return null;

    let element = Expressions.getElementById(currentRoot, elementId);
    let resolvedFromDefault = false;
    if (!element && currentOwnerType) {
      element =
        findSlotDefaultElementAcrossTrees(
          plugins,
          componentMap,
          currentOwnerType,
          elementId,
          currentOwnerElement
        )?.element ?? null;
      resolvedFromDefault = Boolean(element);
    }
    if (!element) return null;

    const componentRoot = getComponentInstRootElement(componentMap)(element);
    if (componentRoot) {
      componentRootPath = [
        ...nativeIdPath.slice(0, pathIndex + 1),
        copyInstanceSuffix(nativeId)(componentRoot.id),
      ];
      if (resolvedFromDefault || isInsideDefaultContent) {
        defaultInstanceRootPath = componentRootPath;
      }
      isInsideDefaultContent = false;
      currentRoot = EElement(componentRoot);
      currentOwnerType = element.type;
      currentOwnerElement = element;
    } else if (resolvedFromDefault) {
      defaultInstanceRootPath = null;
      isInsideDefaultContent = true;
      currentRoot = EElement(element);
      currentOwnerType = null;
      currentOwnerElement = null;
    }

    if (pathIndex !== nativeIdPath.length - 1) continue;

    return {
      componentRootPath,
      nativeIdPathBoundary: componentRootPath.slice(0, -2),
      // A Slot keeps its own Navigator row, as it does inside a live instance.
      defaultInstanceRootPath: isSlot(element) ? null : defaultInstanceRootPath,
    };
  }

  return null;
};

const memoizedResolveSlotDefaultComponentBoundary = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      weakMemo(
        (nativeIdPath: NativeId[]): SlotDefaultComponentBoundary | null => {
          const context = resolveSlotDefaultPathContext(
            nativeIdPath,
            componentMap,
            plugins
          );
          if (!context) return null;

          return resolveComponentBoundaryFromSlotDefaultContext(
            nativeIdPath,
            componentMap,
            plugins,
            context
          );
        }
      )
    )
);

/**
 * Resolves the component boundary for a native ID path that crosses slot-default content.
 *
 * Slot defaults are stored in a component definition's `defaultData`, but the canvas
 * renders them through the owning component's full native ID path. This walks that path
 * and finds the innermost component instance that owns the selected element for Designer
 * interactions, so the element can be treated more like content in a slot override.
 *
 * For example, a path like
 * `[grid-instance, level-two-default, level-three-default, heading]`
 * resolves to a component root path like
 * `[grid-instance, level-two-default, level-three-default, level-three-root]`
 * and a boundary of `[grid-instance, level-two-default]`. The returned `componentRootPath`
 * is used when selecting or outlining the component, while `nativeIdPathBoundary` scopes
 * descendant and binding checks to that component.
 *
 * Returns `null` when the path does not resolve through slot-default content.
 */
export const resolveSlotDefaultComponentBoundary = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): SlotDefaultComponentBoundary | null =>
  memoizedResolveSlotDefaultComponentBoundary(plugins)(componentMap)(
    nativeIdPath
  );

// A default's path leads with its owner, so the outermost instance names the
// owner's row; this names the default's own instance, strictly inside focus.
export const resolveSlotDefaultOwningInstanceRootPath = (
  nativeIdPath: NativeId[] | undefined,
  focusedSymbolNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): NativeId[] | null => {
  if (!nativeIdPath?.length) return null;

  const rootPath = resolveSlotDefaultComponentBoundary(
    nativeIdPath,
    componentMap,
    plugins
  )?.defaultInstanceRootPath;
  if (!rootPath) return null;

  // The last two segments are the instance and its root.
  const isStrictlyInsideFocus =
    rootPath.length - 2 >= focusedSymbolNativeIdPath.length &&
    focusedSymbolNativeIdPath.every(
      (focusedNativeId, index) => rootPath[index] === focusedNativeId
    );
  return isStrictlyInsideFocus ? rootPath : null;
};

/**
 * The path to measure `nativeIdPath` against.
 *
 * Focus wins when the user is inside a component. Otherwise a path running through
 * slot-default content measures from the projected component that owns it, since a
 * default carries an extra segment for its own instance and would otherwise read as
 * one level deeper than equivalent content authored into the slot. Falls back to the
 * focused path when the selection is not slot-default content.
 */
export const resolveSlotDefaultBoundaryOrFocus = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  focusedSymbolNativeIdPath: NativeId[]
): NativeId[] => {
  if (focusedSymbolNativeIdPath.length > 0) return focusedSymbolNativeIdPath;

  const componentBoundary = resolveSlotDefaultComponentBoundary(
    nativeIdPath,
    componentMap,
    plugins
  );
  return componentBoundary?.nativeIdPathBoundary ?? focusedSymbolNativeIdPath;
};

export type SlotDefaultPathResolution = {
  context: SlotDefaultPathContext;
  target: SlotDefaultTargetResolution;
  componentBoundary: SlotDefaultComponentBoundary | null;
};

const resolveSlotDefaultPathUncached = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): SlotDefaultPathResolution | null => {
  const context = resolveSlotDefaultPathContext(
    nativeIdPath,
    componentMap,
    plugins
  );
  if (!context) return null;

  const target = getSlotDefaultTargetElementOrInstance(
    nativeIdPath,
    componentMap,
    plugins
  );
  if (!target) return null;

  const componentBoundary = resolveComponentBoundaryFromSlotDefaultContext(
    nativeIdPath,
    componentMap,
    plugins,
    context
  );
  return {context, target, componentBoundary};
};

// Three path walks per call, and the drag engine asks for the same source path
// on every pointer move.
const memoizedResolveSlotDefaultPath = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      weakMemo((nativeIdPath: NativeId[]) =>
        resolveSlotDefaultPathUncached(nativeIdPath, componentMap, plugins)
      )
    )
);

export const resolveSlotDefaultPath = (
  nativeIdPath: NativeId[] | undefined,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): SlotDefaultPathResolution | null => {
  if (!nativeIdPath?.length) return null;

  return memoizedResolveSlotDefaultPath(plugins)(componentMap)(nativeIdPath);
};

// Convert a slot-default list expression to NodePojos for callers that still need
// the rendered element shape for non-persistence work.
export const lowerSlotDefaultToPojos = (expression: Expression): NodePojo[] => {
  const items = Expressions.getList(expression) || emptyArray;
  return items.reduce<NodePojo[]>((acc, item) => {
    const evaluatedElement = Expressions.evaluate(EvalEnv.emptyEvalEnv, item);
    if (isNodePojo(evaluatedElement)) acc.push(evaluatedElement);
    return acc;
  }, []);
};
